import moment from 'moment';
import { notification } from 'antd';
import REDUCER_KEY from '../reducers/reducer.keys';
import {
  requestClear, requestDirty, requestFailed, requestInFlight, requestReset, requestSuccess
} from './apiActions';

// When a mutually-exclusive tag is assigned, the backend auto-closes the previous value
// and reports it as `superseded`. Surface an info message so the user is aware of the
// expiry date that was applied automatically. No-op for any response without the field.
// exported for unit testing
export const notifySuperseded = (data) => {
  const superseded = data && data.superseded;
  if (!superseded || !superseded.length) {
    return;
  }
  superseded.forEach((item) => {
    notification.info({
      message: 'Previous tag automatically ended',
      description: `The existing ${item.collection} tag "${item.previous_tag}" was automatically ended on `
        + `${moment.parseZone(item.ended).format('D MMM YYYY')} because a new value was assigned from that date.`,
      placement: 'bottomRight',
      duration: 8
    });
  });
};

export const reset = reducer => (dispatch) => {
  dispatch(requestReset(reducer));
};

export const clear = reducer => (dispatch) => {
  dispatch(requestClear(reducer));
};

export const dirty = reducer => (dispatch) => {
  dispatch(requestDirty(reducer));
};

// = ===================================================
// LIST ACTIONS
// = ===================================================

// Several call sites fire a list request while an identical one is still in flight (e.g. a
// reload triggered from a setState callback that overlaps the load it is replacing), so the
// same URL is requested twice and the second response simply overwrites the first. Share the
// pending promise for an identical reducer+params pair instead of issuing a duplicate request.
// The entry is removed as soon as the request settles, so this only collapses *overlapping*
// calls - a later call always hits the network and never sees a cached response.
const inFlightLists = new Map();

export const list = (
  reducer, params
) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  const inFlightParams = {
    ...params
  };
  const requestKey = `${key}|${JSON.stringify(inFlightParams)}`;
  const pending = inFlightLists.get(requestKey);
  if (pending) {
    return pending;
  }

  dispatch(requestInFlight(key, inFlightParams));

  const request = action.list({
    ...params
  })
    .then(
      (response) => {
        inFlightLists.delete(requestKey);
        const data = response.data.toJS();
        dispatch(requestSuccess(data, key));
        return data;
      },
      (error) => {
        inFlightLists.delete(requestKey);
        dispatch(requestFailed(error, key));
        throw error;
      }
    );

  inFlightLists.set(requestKey, request);
  return request;
};

// = ===================================================
// ITEM ACTIONS
// = ===================================================

export const load = (reducer, props) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));

  return action.load(props)
    .then((response) => {
      const data = response.data.toJS();
      dispatch(requestSuccess(data, key));
      return data;
    })
    .catch((error) => {
      dispatch(requestFailed(error, key));
      throw error;
    });
};

export const init = (reducer, params) => (dispatch) => {
  const data = { id: -1, ...params };
  const { key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));
  dispatch(requestSuccess(data, key));
};

export const add = (reducer, params) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));

  return action.add(params)
    .then((response) => {
      const data = response.data ? response.data.toJS() : {};
      dispatch(requestSuccess(data, key));
      notifySuperseded(data);
      return data;
    })
    .catch((error) => {
      dispatch(requestFailed(error, key));
      throw error;
    });
};

export const update = (reducer, params) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));

  return action.update(params)
    .then((response) => {
      const data = response.data ? response.data.toJS() : {};
      dispatch(requestSuccess(data, key));
      notifySuperseded(data);
      return data;
    })
    .catch((error) => {
      dispatch(requestFailed(error, key));
      throw error;
    });
};

export const remove = (reducer, props) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));

  return action.remove(props)
    .then((response) => {
      const { id } = props;
      const { data: body } = response;
      const data = {
        id,
        status: response.status,
        data: body ? body.toJS() : {}
      };
      dispatch(requestReset(key));
      return data;
    })
    .catch((error) => {
      dispatch(requestFailed(error, key));
      throw error;
    });
};

export const clone = (reducer, params) => (dispatch) => {
  const { action, key } = REDUCER_KEY[reducer];
  dispatch(requestInFlight(key));

  return action.clone(params)
    .then((response) => {
      const data = response.data.toJS();
      dispatch(requestSuccess(data, key));
      return data;
    })
    .catch((error) => {
      dispatch(requestFailed(error, key));
      throw error;
    });
};
